/**
 * views/reporting/sections/BidsByYear.jsx - the Bids by Year chart.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, Bar, LabelList, Legend } from 'recharts';
import { Icon } from '../../../components/ui/Icon';
import { exportCsv } from '../../../utils/exportCsv';
import { exportChartPNG } from '../../../utils/exportChartPNG';
import { CSV_BTN } from '../reportingHelpers';
import { CARD, TICK, TT } from '../reportingTokens';
import { ChartFilter } from '../ChartFilter';
import { SectionHeader } from '../SectionHeader';

export function renderBidsByYear({
  bidHistFilter,
  setBidHistFilter,
  bidHistCustomFrom,
  bidHistCustomTo,
  setBidHistCustomFrom,
  setBidHistCustomTo,
  bidsByYear,
  bidHistRef,
  avgWinsPerYear,
}) {
  return (
    <>
      {/* BIDS BY YEAR */}
      <SectionHeader
        title="Bid History, 2021 to Present"
        subtitle="Won vs Lost vs Open by quote year"
        explain={{
          title: 'Bid History',
          body: 'Bar height = COUNT of deals (not dollar value) per status per year. Use this to spot trends in volume — for $ value over time use the Revenue & Pipeline Trend chart below.',
          formula: 'COUNT(*) grouped by YEAR(quote_date) and status',
        }}
        right={
          <>
            <ChartFilter
              value={bidHistFilter}
              onChange={setBidHistFilter}
              showCustom={bidHistFilter === 'custom'}
              customFrom={bidHistCustomFrom}
              customTo={bidHistCustomTo}
              onCustomFromChange={setBidHistCustomFrom}
              onCustomToChange={setBidHistCustomTo}
              onApplyCustom={() => {}}
            />
            <button
              onClick={() =>
                exportCsv(
                  'bid_history.csv',
                  bidsByYear.map((d) => ({ Year: d.year, Won: d.Won, Lost: d.Lost, Open: d.Open }))
                )
              }
              style={CSV_BTN}
            >
              <Icon name="download" size={10} color="#666" /> CSV
            </button>
            <button onClick={() => exportChartPNG(bidHistRef, 'bid_history')} style={CSV_BTN}>
              <Icon name="download" size={10} color="#666" /> PNG
            </button>
          </>
        }
      />
      <div ref={bidHistRef} style={{ ...CARD, marginBottom: 20 }}>
        <div style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bidsByYear} barGap={4} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" vertical={false} />
              <XAxis dataKey="year" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={TT} />
              <ReferenceLine
                y={avgWinsPerYear}
                stroke="#43A04744"
                strokeDasharray="3 3"
                label={{ value: `Avg ${avgWinsPerYear}`, position: 'right', fontSize: 8, fill: '#aaa' }}
              />
              <Bar dataKey="Won" fill="#1B5E20" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="Won"
                  position="top"
                  style={{ fontSize: 10, fontWeight: 800, fill: '#1B5E20' }}
                  formatter={(v) => (v > 0 ? v : '')}
                />
              </Bar>
              <Bar dataKey="Lost" fill="#B71C1C" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="Lost"
                  position="top"
                  style={{ fontSize: 10, fontWeight: 800, fill: '#B71C1C' }}
                  formatter={(v) => (v > 0 ? v : '')}
                />
              </Bar>
              <Bar dataKey="Open" fill="#1565C0" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="Open"
                  position="top"
                  style={{ fontSize: 10, fontWeight: 800, fill: '#1565C0' }}
                  formatter={(v) => (v > 0 ? v : '')}
                />
              </Bar>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, fontFamily: 'Montserrat', fontWeight: 600 }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
